import React from 'react';
import { Stack, Typography, Divider, Box } from '@mui/material';
import { TransferType, TransferCompletionMethod } from '@repo/types';
import { transferCompletionLabel, EntityAvatar } from 'components/UI';
import { stateCardSurfaceSx } from 'pages/CertificatePage/components/stateCardSurface';
import { formatedDate } from 'utils/date';

export interface OwnerProvenanceCardProps {
  name?: string | null;
  /**
   * The secondary line under the name — in practice the owner's `@handle`, or nothing.
   *
   * ⚠️ NEVER AN EMAIL ADDRESS. This prop was called `email` and three of its four call
   * sites duly passed one, which put a real person's address on a page whose entire
   * purpose is to be shared with people who are not that person. Renamed so that the
   * next caller has to notice: `subtext` invites a handle, `email` invited an email.
   * Resolve it with `resolvePublicIdentity` (`utils/parsers`) rather than by hand.
   */
  subtext?: string | null;
  avatarUrl?: string | null;
  sinceLabel?: string | null;
  sinceDate?: string | Date;
  sellPrice?: string | number | null;
  /**
   * VERA-931 — `store` is a place the item passed through, not a party that owned
   * it. It shares the COMPANY avatar treatment (a shop is a business, not a
   * person), but callers must pass it explicitly so the distinction survives into
   * anything that later branches on this.
   */
  ownerType?: 'customer' | 'company' | 'store';
  /**
   * How this owner acquired the certificate (the transfer type). `null` for
   * the company/mint origin row — no "Acquired via" row is rendered then.
   */
  acquiredVia?: TransferType | null;
  /**
   * Per-item production/manufacture date (VERA-924). Only ever set on the
   * company/origin row, and only when a real date exists — no "Manufactured"
   * row is rendered otherwise.
   */
  manufacturedDate?: string | Date | null;
  /**
   * VERA-304 (Slice 3a) — evidence level this transfer completed with. `null`
   * for the company/mint origin row — no "Verification" row is rendered then.
   */
  completionMethod?: TransferCompletionMethod | null;
}

const ACQUIRED_VIA_LABELS: Record<TransferType, string> = {
  [TransferType.Primary]: 'Purchased new',
  [TransferType.Sell]: 'Purchased resale',
  [TransferType.Gift]: 'Gifted',
};

export const OwnerProvenanceCard: React.FC<OwnerProvenanceCardProps> = ({
  name,
  subtext,
  avatarUrl,
  sinceLabel = 'Owner since',
  sinceDate,
  sellPrice,
  ownerType = 'customer',
  acquiredVia,
  manufacturedDate,
  completionMethod,
}) => {
  /*
   * VERA-304: the local `initials` memo is gone. It was a character-for-character copy
   * of `EntityAvatar`'s own `deriveInitials` — which is what this passes them TO — and
   * the copies had already diverged: `deriveInitials` now strips a leading '@' so a
   * handle initials as "SP" rather than "@S", and this one did not, so the same person
   * rendered differently here than in the list row (§36). Passing no `initials` lets
   * the shared component derive them, which is the one implementation.
   */
  return (
    <Stack
      sx={{
        /*
         * The certificate page's CARD SURFACE, taken from the one source the
         * page's state cards already share (`stateCardSurfaceSx`) rather than
         * hand-rolled a fourth time (§36/§47 — reuse, and improve the shared
         * thing, before inventing).
         *
         * The defect this fixes is the same one `stateCardSurface.ts` documents
         * for the transfer card, and it was still live here: a white card whose
         * ONLY separation from the page was a 1px hairline, with no shadow to lift
         * it. That separation is not something this card can rely on, because the
         * page behind it is `background.default` — `#F7F7F8` on the default theme,
         * but TENANT-CONTROLLED (`bg_color`), so any brand that picks white
         * collapses it to the 1.00:1 no-boundary case `stateCardSurface.ts`
         * measured. A shadow is the part that holds at every tenant value.
         *
         * The surface, before and after:
         *
         * | | before | after |
         * |---|---|---|
         * | background | `#FFFFFF` (`common.white`) | `#FFFFFF` (`background.paper`) |
         * | border | `1px solid` grey[300] → renders `#E4E4E7` | `1px solid #E4E4E7` |
         * | radius | `8px` | `12px` |
         * | shadow | **none** | `elevation.card` |
         *
         * So the visible change is the SHADOW plus 4px of radius; the background
         * and the RENDERED border colour are unchanged. The authored border colour
         * was already being discarded — `index.css`'s `#vera-shadow-root *` applies
         * `border-border` (`--border: 240 6% 90%` = #E4E4E7) at ID specificity, and
         * the shorthand form loses to it exactly as a separate `borderColor` would,
         * so this app's `grey[300]` (#b0b0b0 — not MUI's default) never painted and
         * the card has always drawn #E4E4E7. The new value is therefore honest about
         * what renders rather than a change to it. Routing through
         * `background.paper` instead of `common.white` also stops the card
         * hardcoding a surface a tenant's theme is meant to own (`settings/tokens`).
         */
        ...stateCardSurfaceSx,
        /*
         * …minus the card's own block spacing. A state card is a single block that
         * owns the gap beneath it; provenance cards are a LIST whose gap is owned
         * by the parent (`Provenance.tsx`'s `<Stack spacing={1.5}>` = 12px, and
         * `PublicCertificateSections`' per-item `mb: 2`). Inheriting `mb: 2` would
         * add 16px to every gap. Same override, same reason, as `ActionTileRow`.
         */
        mb: 0,
      }}
    >
      {/* Top: avatar, name, @handle */}
      <Stack direction='row' alignItems='center' spacing={1.5}>
        {/* The origin row of a provenance chain is the BRAND, a store row is a
            PLACE, and every other row is a person — so this one component decides
            the shape per row from `ownerType`. The brand mark was previously
            circle-cropped here.

            VERA-931: the test is now "is this a person", not "is this a company".
            A store took the person shape under the old form, which put a circular
            portrait avatar on a shop. */}
        <EntityAvatar
          entity={ownerType === 'customer' ? 'person' : 'company'}
          src={avatarUrl}
          name={name}
          size={40}
        />
        <Stack spacing={0} sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 400, color: 'text.primary' }}>
            {name || '—'}
          </Typography>
          {subtext && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{subtext}</Typography>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ my: 1, ml: '52px', borderColor: theme => theme.palette.grey[300] }} />

      {/* Bottom: labels and values */}
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ ml: '52px' }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{sinceLabel}</Typography>
        <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
          {/* `formatedDate` falls back to `new Date()` for an empty value, which would
              fabricate "owned since today" for rows with no real date (e.g. the mint/origin
              row when the product has no `manufactured_at`). Render a dash instead. */}
          {sinceDate ? formatedDate({ date: sinceDate, format: 'MMM do yyyy, h:mm a' }) : '—'}
        </Typography>
      </Stack>
      {acquiredVia !== undefined && acquiredVia !== null && (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mt={0.5}
          sx={{ ml: '52px' }}
        >
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Acquired via</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
            {ACQUIRED_VIA_LABELS[acquiredVia] || acquiredVia}
          </Typography>
        </Stack>
      )}
      {completionMethod !== undefined && completionMethod !== null && (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mt={0.5}
          sx={{ ml: '52px' }}
        >
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Verification</Typography>
          {/*
           * PLAIN TEXT, not a chip. This is the third row of a three-row table whose
           * other two values ("Owner since", "Acquired via") are bare right-aligned
           * text, and a pill here read as a widget parked in a table rather than as
           * that table's third value.
           *
           * The icon went WITH the pill rather than surviving it. `AuthenticChip`'s
           * glyph is legible because the pill's colour says which of two states it is
           * in; stripped of the fill, a lone mark beside a word is an unexplained
           * decoration — and it is the only glyph in the block, which reinstates
           * exactly the "one row is a widget" reading the pill was removed for. The
           * three strings carry the evidence ladder on their own, which is why the
           * label map survives the chip (`transferCompletionLabel`).
           */}
          <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
            {transferCompletionLabel(completionMethod)}
          </Typography>
        </Stack>
      )}
      {manufacturedDate !== undefined && manufacturedDate !== null && (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mt={0.5}
          sx={{ ml: '52px' }}
        >
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Manufactured</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
            {formatedDate({ date: manufacturedDate, format: 'MMM do yyyy, h:mm a' })}
          </Typography>
        </Stack>
      )}
      {sellPrice !== undefined && sellPrice !== null && (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mt={0.5}
          sx={{ ml: '52px' }}
        >
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Sell price</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{String(sellPrice)}</Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default OwnerProvenanceCard;
